/**
 * Top-level Sales Comps page.
 * Two-panel layout: interactive Leaflet map (left) + search controls and results grid (right).
 * Accessible from main nav alongside Deals — independent of any deal context.
 */

import { useState, useCallback, useRef } from "react";
import { MapPin } from "lucide-react";
import CompsMap from "../components/comps/CompsMap";
import CompsSearchBar from "../components/comps/CompsSearchBar";
import CompsGrid from "../components/comps/CompsGrid";
import type { CompSearchRequest, CompSearchResponse } from "../api/comps";
import { searchComps } from "../api/comps";

export default function CompsPage() {
  const [geofenceRings, setGeofenceRings] = useState<number[][][] | null>(null);
  const [response, setResponse] = useState<CompSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedParcelId, setSelectedParcelId] = useState<string | null>(null);
  const searchParamsRef = useRef<Omit<CompSearchRequest, "geometry"> | null>(null);

  const handleGeofenceDrawn = useCallback((rings: number[][][]) => {
    setGeofenceRings(rings);
  }, []);

  const handleGeofenceClear = useCallback(() => {
    setGeofenceRings(null);
    setResponse(null);
    setError(null);
  }, []);

  const handleSearch = useCallback(
    async (params: CompSearchRequest) => {
      if (!geofenceRings) return;
      setLoading(true);
      setError(null);
      setSelectedParcelId(null);

      // Store params for potential re-search
      searchParamsRef.current = params;

      const request: CompSearchRequest = {
        ...params,
        geometry: { rings: geofenceRings },
      };

      try {
        const result = await searchComps(request);
        setResponse(result);
      } catch (err: any) {
        const msg = err?.message || "Search failed. Please try again.";
        setError(msg);
        setResponse(null);
      } finally {
        setLoading(false);
      }
    },
    [geofenceRings]
  );

  const handleClear = useCallback(() => {
    setResponse(null);
    setError(null);
    setSelectedParcelId(null);
  }, []);

  const handleMarkerClick = useCallback((parcelId: string) => {
    setSelectedParcelId(parcelId);
  }, []);

  const handleRowClick = useCallback((parcelId: string) => {
    setSelectedParcelId(parcelId);
  }, []);

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-6">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-forest-100 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-forest-700" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-bark-800">
            Sales Comps
          </h1>
          <p className="text-sm text-bark-500">
            Search commercial property sales from Florida county records
          </p>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left panel: Map */}
        <div className="lg:w-[40%] min-h-[500px]">
          <CompsMap
            onGeofenceDrawn={handleGeofenceDrawn}
            onGeofenceCleared={handleGeofenceClear}
            results={response?.results || []}
            selectedParcelId={selectedParcelId}
            onMarkerClick={handleMarkerClick}
          />
        </div>

        {/* Right panel: Controls + Grid */}
        <div className="lg:w-[60%] space-y-4">
          <CompsSearchBar
            hasGeofence={geofenceRings != null}
            onSearch={handleSearch}
            onClear={handleClear}
            loading={loading}
          />

          {error && (
            <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
              {error}
            </div>
          )}

          <CompsGrid
            response={response}
            loading={loading}
            selectedParcelId={selectedParcelId}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
    </div>
  );
}
